/**
 * Saved Views + share link. A "view" is a reusable chart recipe - plot type,
 * axes, color, style, derived signals, fits, filters - serialized via the same
 * query string `syncToUrl`/`restoreFromUrl` use. By DEFAULT a view stores only
 * that recipe (ship + date range stripped), so loading it applies the recipe to
 * whatever ship/range you currently have open ("scatter PP vs STW", on any
 * ship). Opting into "pin ship & date range" stores those too (`bindContext`),
 * reproducing a full snapshot. Loading routes back through `restoreFromUrl`, so
 * views and shareable links share one code path.
 *
 * Pure-ish module: localStorage CRUD, context (de)composition, clipboard copy,
 * and the reset-then-restore loader. The header drawer (`presets/presets-bar.ts`)
 * is the only UI. `activeViewId` is an atom so the drawer + chips can highlight
 * the loaded view and offer "Update".
 */

import {
  activeTab,
  derivedSignals,
  filters,
  filterLogic,
  filterExpr,
  filterExprError,
  filterMode,
  outliers,
  DEFAULT_OUTLIERS,
  selectedCols,
  exportFormat,
  fillMethod,
  frequencyValue,
  frequencyUnit,
  selectedShip,
  comparisonShips,
  startDate,
  endDate,
  activePreset,
  Atom,
} from './store.ts';
import { resetPlotStateToDefaults, isPlotEnabled } from './plot-state.ts';
import { buildQueryString, syncToUrl, restoreFromUrl } from './url-sync.ts';
import { doPlot } from '../features/plot/plot-actions.ts';
import { plotFetching } from '../features/plot/plot-ui-state.ts';
import { showToast } from '../ui/toast.ts';
import { longCurrentViewUrl, shortenCurrentView } from './share-link.ts';
import { userMessage } from '../api/errors.ts';

export const VIEWS_KEY = 'neptune_view_presets_v2';
const VIEWS_KEY_V1 = 'neptune_view_presets_v1';

/** Params that describe *context* (which ship / when), not the chart recipe. */
const CONTEXT_PARAMS = ['ship', 'ships', 'start', 'end', 'preset', 'pc', 'sel', 'tab'] as const;

export interface ViewContext {
  ship?: string;
  /** Comma-separated comparison ships plotted alongside `ship`. */
  ships?: string;
  start?: string;
  end?: string;
  preset?: string;
  live?: string;
  pc?: string;
}

export interface SavedView {
  id: string;
  name: string;
  /** Config-only query (context stripped) unless `bindContext`. */
  query: string;
  /** When true, `context` is stored and applied on load (full snapshot). */
  bindContext: boolean;
  context: ViewContext | null;
  plotType: string;
  pinned: boolean;
  isDefault: boolean;
  order: number;
}

/** Id of the currently-loaded view (drives highlight + the Update affordance). */
export const activeViewId = new Atom<string | null>(null);

/* ── ids ─────────────────────────────────────────────────── */

function genId(): string {
  return 'v' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

/* ── localStorage CRUD (+ v1 migration) ──────────────────── */

export function loadViews(): SavedView[] {
  try {
    const raw = localStorage.getItem(VIEWS_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? (arr as SavedView[]) : [];
    }
    // One-time migration from v1 full-snapshot presets → bound v2 views, so
    // they keep their original jump-to-ship/range behavior.
    const rawV1 = localStorage.getItem(VIEWS_KEY_V1);
    if (rawV1) {
      const old = JSON.parse(rawV1);
      if (Array.isArray(old) && old.length) {
        const migrated: SavedView[] = old.map((p: { name?: string; query?: string }, i: number) => ({
          id: genId(),
          name: p.name || `View ${i + 1}`,
          query: stripContext(p.query || ''),
          bindContext: true,
          context: extractContext(p.query || ''),
          plotType: new URLSearchParams(p.query || '').get('pt') || 'line',
          pinned: false,
          isDefault: false,
          order: i,
        }));
        saveViews(migrated);
        return migrated;
      }
    }
    return [];
  } catch {
    return [];
  }
}

function saveViews(list: SavedView[]): void {
  try {
    localStorage.setItem(VIEWS_KEY, JSON.stringify(list));
  } catch {
    showToast('error', 'Could not save view - localStorage is unavailable');
  }
}

/** Views sorted by their stored display order. */
export function sortedViews(): SavedView[] {
  return loadViews()
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

function nextOrder(list: SavedView[]): number {
  return list.reduce((m, v) => Math.max(m, (v.order ?? 0) + 1), 0);
}

/* ── context (de)composition ─────────────────────────────── */

/** The current view as the internal query-string wire format. */
function currentViewQuery(): string {
  return buildQueryString();
}

/** Drop context params, keeping only the chart recipe. */
export function stripContext(query: string): string {
  const p = new URLSearchParams(query);
  for (const k of CONTEXT_PARAMS) p.delete(k);
  return p.toString();
}

export function extractContext(query: string): ViewContext {
  const p = new URLSearchParams(query);
  const ctx: ViewContext = {};
  for (const k of ['ship', 'ships', 'start', 'end', 'preset', 'pc'] as const) {
    const v = p.get(k);
    if (v != null) ctx[k] = v;
  }
  return ctx;
}

/**
 * Re-attach the user's current ships + date range to a config-only query, so
 * saved views and unpinned Catalog charts restore the full open selection.
 */
export function withCurrentContext(query: string): string {
  const p = new URLSearchParams(query);
  if (selectedShip.value) p.set('ship', selectedShip.value);
  const extras = comparisonShips.value.filter((sh) => sh && sh !== selectedShip.value);
  if (extras.length) p.set('ships', extras.join(','));
  if (activePreset.value !== 'custom') {
    p.set('preset', activePreset.value);
  } else {
    if (startDate.value) p.set('start', startDate.value);
    if (endDate.value) p.set('end', endDate.value);
  }
  return p.toString();
}

/** A short "type · columns · scope" description for a view's recipe. */
export function viewSummary(view: SavedView): string {
  const p = new URLSearchParams(view.query || '');
  const pt = p.get('pt') || 'line';
  const parts: string[] = [pt];
  if (pt === 'histogram') {
    const hc = (p.get('hc') || '').split(',').filter(Boolean);
    parts.push(hc.length ? hc.join(', ') : 'no columns');
  } else if (pt === 'scatter') {
    const x = p.get('x') || '-';
    const y = (p.get('yl') || '').split(',').filter(Boolean);
    parts.push(`${y.length ? y.join(', ') : '?'} vs ${x}`);
  } else {
    const cols = [...(p.get('yl') || '').split(','), ...(p.get('yr') || '').split(',')].filter(
      Boolean,
    );
    parts.push(cols.length ? cols.join(', ') : 'no series');
  }
  if (view.bindContext && view.context?.ship) {
    const c = view.context;
    const extra = c.ships ? c.ships.split(',').filter(Boolean).length : 0;
    const shipLabel = extra ? `${c.ship} +${extra}` : c.ship;
    parts.push(`${shipLabel} · ${c.preset || (c.start ? 'custom range' : 'all')}`);
  } else {
    parts.push('any ship / range');
  }
  return parts.join(' · ');
}

/* ── Save / update / delete / mutate ─────────────────────── */

/**
 * Save the current view under `name` (overwriting any same-named view). By
 * default config-only; `pinContext` also stores the current ship + date range.
 */
export function saveCurrentView(name: string, pinContext: boolean): void {
  const full = currentViewQuery();
  const configQuery = stripContext(full);
  if (!configQuery) {
    showToast('error', 'Nothing to save - pick a plot type and some columns first');
    return;
  }
  const list = loadViews();
  const at = list.findIndex((v) => v.name === name);
  const prev = at >= 0 ? list[at] : null;
  const entry: SavedView = {
    id: prev ? prev.id : genId(),
    name,
    query: configQuery,
    bindContext: pinContext,
    context: pinContext ? extractContext(full) : null,
    plotType: new URLSearchParams(configQuery).get('pt') || 'line',
    pinned: prev ? prev.pinned : false,
    isDefault: prev ? prev.isDefault : false,
    order: prev ? prev.order : nextOrder(list),
  };
  if (at >= 0) list[at] = entry;
  else list.push(entry);
  saveViews(list);
  activeViewId.set(entry.id);
  showToast('success', at >= 0 ? `Updated view "${name}"` : `Saved view "${name}"`);
}

/** Re-save the current chart config into the active view (no re-typing). */
export function updateActiveView(): void {
  const id = activeViewId.value;
  if (!id) return;
  const list = loadViews();
  const v = list.find((x) => x.id === id);
  if (!v) return;
  const full = currentViewQuery();
  v.query = stripContext(full);
  v.plotType = new URLSearchParams(v.query).get('pt') || 'line';
  if (v.bindContext) v.context = extractContext(full);
  saveViews(list);
  activeViewId.notify();
  showToast('success', `Updated "${v.name}"`);
}

export function deleteView(id: string): void {
  const list = loadViews();
  const at = list.findIndex((v) => v.id === id);
  if (at < 0) return;
  const [removed] = list.splice(at, 1);
  if (!removed) return;
  saveViews(list);
  if (activeViewId.value === id) activeViewId.set(null);
  activeViewId.notify();
  showToast('success', `Deleted "${removed.name}"`);
}

export function renameView(id: string, name: string): void {
  const trimmed = name.trim();
  if (!trimmed) return;
  const list = loadViews();
  const v = list.find((x) => x.id === id);
  if (!v || v.name === trimmed) return;
  v.name = trimmed.slice(0, 60);
  saveViews(list);
  activeViewId.notify();
}

export function toggleViewPin(id: string): void {
  const list = loadViews();
  const v = list.find((x) => x.id === id);
  if (!v) return;
  v.pinned = !v.pinned;
  saveViews(list);
  activeViewId.notify();
}

export function toggleViewDefault(id: string): void {
  const list = loadViews();
  const v = list.find((x) => x.id === id);
  if (!v) return;
  const turningOn = !v.isDefault;
  for (const x of list) x.isDefault = false;
  v.isDefault = turningOn;
  saveViews(list);
  activeViewId.notify();
  showToast('success', turningOn ? `"${v.name}" will auto-load on open` : 'Default view cleared');
}

/** Move the view with `fromId` to the slot occupied by `toId`. */
export function reorderViews(fromId: string, toId: string): void {
  const list = sortedViews();
  const from = list.findIndex((v) => v.id === fromId);
  if (from < 0 || fromId === toId) return;
  const [moved] = list.splice(from, 1);
  if (!moved) return;
  const to = list.findIndex((v) => v.id === toId);
  list.splice(to < 0 ? list.length : to, 0, moved);
  list.forEach((v, i) => (v.order = i));
  saveViews(list);
  activeViewId.notify();
}

/* ── Reset + load ────────────────────────────────────────── */

/**
 * Reset every view atom to its page-load default. Broader than the plot-only
 * `resetPlotStateToDefaults`: also clears filters, derived signals, the export
 * column selection, and the format/freq/fill controls, and snaps back to the
 * Export tab. Setting atoms directly never triggers `syncToUrl` (only the
 * mutator helpers do), so this is silent - the restore that follows owns the URL.
 *
 * Ship and date range are intentionally left alone; the loader supplies the
 * right context (the view's own, or the current one for config-only views).
 */
export function resetViewToDefaults(): void {
  resetPlotStateToDefaults();
  derivedSignals.set([]);
  filters.set([]);
  filterLogic.set('AND');
  filterExpr.set('');
  filterExprError.set(null);
  filterMode.set('and');
  outliers.set({ ...DEFAULT_OUTLIERS });
  selectedCols.set(new Set());
  exportFormat.set('csv');
  fillMethod.set('none');
  frequencyValue.set('');
  frequencyUnit.set('min');
  if (activeTab.value !== 'export') {
    activeTab.set('export');
    document.body.dataset.activeTab = 'export';
  }
}

/**
 * Load a view: wipe to defaults, build the restore query (the view's own ship +
 * range when bound, otherwise the current context), restore it, and - if we land
 * on the Plot tab with a plottable selection - auto-render. Columns absent from
 * the resulting ship's schema are pruned by `restoreFromUrl`.
 */
export async function loadView(id: string): Promise<void> {
  const v = loadViews().find((x) => x.id === id);
  if (!v || !v.query) return;

  // Wipe FIRST so the additive restore doesn't inherit stale filters/derived/fits.
  resetViewToDefaults();

  let query: string;
  if (v.bindContext && v.context && Object.keys(v.context).length) {
    const p = new URLSearchParams(v.query);
    for (const [k, val] of Object.entries(v.context)) if (val != null) p.set(k, val);
    query = p.toString();
  } else {
    query = withCurrentContext(v.query);
  }

  try {
    // Pass the query explicitly so an intermediate syncToUrl can't clobber it.
    await restoreFromUrl(query);
    activeViewId.set(id);
    // A saved view is a chart recipe, so surface the chart: jump to the Plot tab
    // and render it. (`resetViewToDefaults` parks us on Export, and the view's
    // query has no `tab` param - it's stripped as context - so without this the
    // restored chart would sit invisible behind the Export tab.) Views with no
    // plottable selection stay on Export.
    if (isPlotEnabled()) {
      if (activeTab.value !== 'plot') {
        activeTab.set('plot');
        document.body.dataset.activeTab = 'plot';
      }
      if (!plotFetching.value) void doPlot();
    }
    showToast('success', `Loaded "${v.name}"`);
  } catch (e) {
    const msg = userMessage(e);
    showToast('error', 'Failed to load view: ' + msg);
  }
}

/** Auto-load the user's default view on a fresh open (URL carries no view). */
export async function maybeAutoLoadDefaultView(skip = false): Promise<void> {
  if (skip) return;
  const def = loadViews().find((v) => v.isDefault);
  if (def) await loadView(def.id);
}

/* ── Export / import ─────────────────────────────────────── */

export function exportViews(): void {
  const list = loadViews();
  if (!list.length) {
    showToast('error', 'No views to export');
    return;
  }
  const blob = new Blob([JSON.stringify(list, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'data-explorer-views.json';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  showToast('success', `Exported ${list.length} view${list.length === 1 ? '' : 's'}`);
}

export async function importViews(file: File): Promise<void> {
  try {
    const text = await file.text();
    const incoming = JSON.parse(text);
    if (!Array.isArray(incoming)) throw new Error('Expected a JSON array');
    const list = loadViews();
    let added = 0;
    let updated = 0;
    for (const raw of incoming as Array<Partial<SavedView>>) {
      if (!raw || !raw.name || !raw.query) continue;
      const entry: SavedView = {
        id: typeof raw.id === 'string' ? raw.id : genId(),
        name: String(raw.name).slice(0, 60),
        query: stripContext(String(raw.query)),
        bindContext: !!raw.bindContext,
        context: raw.context ?? null,
        plotType: raw.plotType || new URLSearchParams(String(raw.query)).get('pt') || 'line',
        pinned: !!raw.pinned,
        isDefault: false,
        order: 0,
      };
      const at = list.findIndex((v) => v.name === entry.name);
      const prev = at >= 0 ? list[at] : undefined;
      if (prev) {
        entry.order = prev.order;
        list[at] = entry;
        updated++;
      } else {
        entry.order = nextOrder(list);
        list.push(entry);
        added++;
      }
    }
    saveViews(list);
    activeViewId.notify();
    showToast('success', `Imported ${added} new, updated ${updated}`);
  } catch (e) {
    const msg = userMessage(e);
    showToast('error', 'Import failed: ' + msg);
  }
}

/* ── Share link ──────────────────────────────────────────── */

/**
 * Persist the current draft, copy a portable link, and toast the result.
 *
 * Prefers the short `/s/<id>` form. That needs the backend's metadata database,
 * so when there isn't one - or it is down, or the view is empty - this falls
 * back to a full legacy URL assembled only for the clipboard. The normal
 * address bar remains clean and every older app version can still open it.
 */
export async function copyShareLink(
  opts: { pinContext?: boolean } = {},
): Promise<boolean> {
  syncToUrl();
  const pinContext = opts.pinContext ?? true;
  // Unpinned, the short link carries the recipe only - the server strips ship
  // and range before hashing, so it also dedupes against every other unpinned
  // copy of the same chart. There is no unpinned form of the long URL, so that
  // fallback always carries the current in-memory context.
  const url = (await shortenCurrentView({ pinContext })) ?? longCurrentViewUrl();
  let ok = false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      ok = true;
    } else {
      // Fallback for insecure contexts / older browsers.
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      ok = document.execCommand('copy');
      document.body.removeChild(ta);
    }
  } catch {
    ok = false;
  }
  showToast(
    ok ? 'success' : 'error',
    ok ? 'Link copied to clipboard' : 'Copy failed - please try Share again',
  );
  return ok;
}
